import {
  collection,
  doc,
  getDoc,
  getDocs,
  addDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  serverTimestamp,
  Timestamp
} from 'firebase/firestore';
import { db } from '../config/firebase';
import { Page, CreatePageData, UpdatePageData } from '../types/page';

// الحصول على مرجع مجموعة الصفحات
const pagesCollection = collection(db, 'pages');

/**
 * الحصول على جميع الصفحات
 * @returns قائمة بجميع الصفحات
 */
export const getAllPages = async (): Promise<Page[]> => {
  try {
    const q = query(pagesCollection, orderBy('order', 'asc'));
    const querySnapshot = await getDocs(q);
    
    return querySnapshot.docs.map(doc => {
      const data = doc.data();
      return {
        id: doc.id,
        title: data.title,
        slug: data.slug,
        content: data.content,
        metaTitle: data.metaTitle,
        metaDescription: data.metaDescription,
        isActive: data.isActive,
        showInFooter: data.showInFooter,
        showInHeader: data.showInHeader,
        footerSection: data.footerSection,
        order: data.order,
        createdAt: data.createdAt?.toDate() || new Date(),
        updatedAt: data.updatedAt?.toDate() || new Date()
      } as Page;
    });
  } catch (error) {
    console.error('Error getting pages:', error);
    throw error;
  }
};

/**
 * الحصول على الصفحات النشطة
 * @returns قائمة بالصفحات النشطة
 */
export const getActivePages = async (): Promise<Page[]> => {
  try {
    const q = query(
      pagesCollection,
      where('isActive', '==', true),
      orderBy('order', 'asc')
    );
    const querySnapshot = await getDocs(q);
    
    return querySnapshot.docs.map(doc => {
      const data = doc.data();
      return {
        id: doc.id,
        title: data.title,
        slug: data.slug,
        content: data.content,
        metaTitle: data.metaTitle,
        metaDescription: data.metaDescription,
        isActive: data.isActive,
        showInFooter: data.showInFooter,
        showInHeader: data.showInHeader,
        footerSection: data.footerSection,
        order: data.order,
        createdAt: data.createdAt?.toDate() || new Date(),
        updatedAt: data.updatedAt?.toDate() || new Date()
      } as Page;
    });
  } catch (error) {
    console.error('Error getting active pages:', error);
    throw error;
  }
};

/**
 * الحصول على الصفحات التي تظهر في الفوتر
 * @returns قائمة بالصفحات التي تظهر في الفوتر
 */
export const getFooterPages = async (): Promise<Page[]> => {
  try {
    const q = query(
      pagesCollection,
      where('isActive', '==', true),
      where('showInFooter', '==', true),
      orderBy('order', 'asc')
    );
    const querySnapshot = await getDocs(q);
    
    return querySnapshot.docs.map(doc => {
      const data = doc.data();
      return {
        id: doc.id,
        title: data.title,
        slug: data.slug,
        content: data.content,
        metaTitle: data.metaTitle,
        metaDescription: data.metaDescription,
        isActive: data.isActive,
        showInFooter: data.showInFooter,
        showInHeader: data.showInHeader,
        footerSection: data.footerSection,
        order: data.order,
        createdAt: data.createdAt?.toDate() || new Date(),
        updatedAt: data.updatedAt?.toDate() || new Date()
      } as Page;
    });
  } catch (error) {
    console.error('Error getting footer pages:', error);
    throw error;
  }
};

/**
 * الحصول على الصفحات التي تظهر في الهيدر
 * @returns قائمة بالصفحات التي تظهر في الهيدر
 */
export const getHeaderPages = async (): Promise<Page[]> => {
  try {
    const q = query(
      pagesCollection,
      where('isActive', '==', true),
      where('showInHeader', '==', true),
      orderBy('order', 'asc')
    );
    const querySnapshot = await getDocs(q);
    
    return querySnapshot.docs.map(doc => {
      const data = doc.data();
      return {
        id: doc.id,
        title: data.title,
        slug: data.slug,
        content: data.content,
        metaTitle: data.metaTitle,
        metaDescription: data.metaDescription,
        isActive: data.isActive,
        showInFooter: data.showInFooter,
        showInHeader: data.showInHeader,
        footerSection: data.footerSection,
        order: data.order,
        createdAt: data.createdAt?.toDate() || new Date(),
        updatedAt: data.updatedAt?.toDate() || new Date()
      } as Page;
    });
  } catch (error) {
    console.error('Error getting header pages:', error);
    throw error;
  }
};

/**
 * الحصول على صفحة بواسطة المعرف
 * @param id معرف الصفحة
 * @returns بيانات الصفحة
 */
export const getPageById = async (id: string): Promise<Page | null> => {
  try {
    const pageDoc = await getDoc(doc(pagesCollection, id));
    
    if (!pageDoc.exists()) {
      return null;
    }
    
    const data = pageDoc.data();
    return {
      id: pageDoc.id,
      title: data.title,
      slug: data.slug,
      content: data.content,
      metaTitle: data.metaTitle,
      metaDescription: data.metaDescription,
      isActive: data.isActive,
      showInFooter: data.showInFooter,
      showInHeader: data.showInHeader,
      footerSection: data.footerSection,
      order: data.order,
      createdAt: data.createdAt?.toDate() || new Date(),
      updatedAt: data.updatedAt?.toDate() || new Date()
    } as Page;
  } catch (error) {
    console.error('Error getting page by ID:', error);
    throw error;
  }
};

/**
 * الحصول على صفحة بواسطة الرابط المختصر
 * @param slug الرابط المختصر للصفحة
 * @returns بيانات الصفحة
 */
export const getPageBySlug = async (slug: string): Promise<Page | null> => {
  try {
    const q = query(pagesCollection, where('slug', '==', slug));
    const querySnapshot = await getDocs(q);
    
    if (querySnapshot.empty) {
      return null;
    }
    
    const pageDoc = querySnapshot.docs[0];
    const data = pageDoc.data();
    
    return {
      id: pageDoc.id,
      title: data.title,
      slug: data.slug,
      content: data.content,
      metaTitle: data.metaTitle,
      metaDescription: data.metaDescription,
      isActive: data.isActive,
      showInFooter: data.showInFooter,
      showInHeader: data.showInHeader,
      footerSection: data.footerSection,
      order: data.order,
      createdAt: data.createdAt?.toDate() || new Date(),
      updatedAt: data.updatedAt?.toDate() || new Date()
    } as Page;
  } catch (error) {
    console.error('Error getting page by slug:', error);
    throw error;
  }
};

/**
 * إنشاء صفحة جديدة
 * @param pageData بيانات الصفحة الجديدة
 * @returns معرف الصفحة الجديدة
 */
export const createPage = async (pageData: CreatePageData): Promise<string> => {
  try {
    // التحقق من عدم وجود صفحة بنفس الرابط المختصر
    const existingPage = await getPageBySlug(pageData.slug);
    if (existingPage) {
      throw new Error('يوجد صفحة أخرى بنفس الرابط المختصر');
    }
    
    const docRef = await addDoc(pagesCollection, {
      ...pageData,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    });
    
    return docRef.id;
  } catch (error) {
    console.error('Error creating page:', error);
    throw error;
  }
};

/**
 * تحديث صفحة
 * @param id معرف الصفحة
 * @param pageData بيانات التحديث
 */
export const updatePage = async (id: string, pageData: UpdatePageData): Promise<void> => {
  try {
    // التحقق من عدم وجود صفحة أخرى بنفس الرابط المختصر
    if (pageData.slug) {
      const existingPage = await getPageBySlug(pageData.slug);
      if (existingPage && existingPage.id !== id) {
        throw new Error('يوجد صفحة أخرى بنفس الرابط المختصر');
      }
    }
    
    const pageRef = doc(pagesCollection, id);
    await updateDoc(pageRef, {
      ...pageData,
      updatedAt: serverTimestamp()
    });
  } catch (error) {
    console.error('Error updating page:', error);
    throw error;
  }
};

/**
 * حذف صفحة
 * @param id معرف الصفحة
 */
export const deletePage = async (id: string): Promise<void> => {
  try {
    await deleteDoc(doc(pagesCollection, id));
  } catch (error) {
    console.error('Error deleting page:', error);
    throw error;
  }
};

/**
 * إنشاء الصفحات الافتراضية إذا لم تكن موجودة
 */
export const createDefaultPages = async (): Promise<void> => {
  try {
    const pages = await getAllPages();
    
    // إذا كانت هناك صفحات موجودة بالفعل، لا نقوم بإنشاء الصفحات الافتراضية
    if (pages.length > 0) {
      return;
    }
    
    // إنشاء صفحة "من نحن"
    await createPage({
      title: 'من نحن',
      slug: 'about',
      content: `
        <h1>من نحن</h1>
        <p>مرحبًا بكم في متجرنا الإلكتروني المتخصص في بيع وصيانة الأجهزة الإلكترونية والكهربائية.</p>
        <p>نحن نسعى دائمًا لتقديم أفضل المنتجات وخدمات الصيانة بأعلى جودة وأفضل الأسعار.</p>
        <h2>رؤيتنا</h2>
        <p>أن نكون الخيار الأول للعملاء في مجال بيع وصيانة الأجهزة الإلكترونية والكهربائية.</p>
        <h2>رسالتنا</h2>
        <p>تقديم منتجات وخدمات عالية الجودة تلبي احتياجات العملاء وتفوق توقعاتهم.</p>
        <h2>قيمنا</h2>
        <ul>
          <li>الجودة: نلتزم بتقديم منتجات وخدمات عالية الجودة.</li>
          <li>الأمانة: نتعامل بشفافية ومصداقية مع عملائنا.</li>
          <li>خدمة العملاء: نضع رضا العملاء في مقدمة أولوياتنا.</li>
          <li>الابتكار: نسعى دائمًا لتطوير خدماتنا ومنتجاتنا.</li>
        </ul>
      `,
      metaTitle: 'من نحن | متجر الإلكترونيات',
      metaDescription: 'تعرف على متجرنا المتخصص في بيع وصيانة الأجهزة الإلكترونية والكهربائية',
      isActive: true,
      showInFooter: true,
      showInHeader: true,
      footerSection: 'quick_links',
      order: 1
    });
    
    // إنشاء صفحة "اتصل بنا"
    await createPage({
      title: 'اتصل بنا',
      slug: 'contact',
      content: `
        <h1>اتصل بنا</h1>
        <p>يسعدنا تواصلكم معنا عبر أي من وسائل الاتصال التالية:</p>
        <h2>معلومات الاتصال</h2>
        <ul>
          <li><strong>العنوان:</strong> شارع الملك فهد، الرياض، المملكة العربية السعودية</li>
          <li><strong>رقم الهاتف:</strong> +966 12 345 6789</li>
          <li><strong>البريد الإلكتروني:</strong> info@example.com</li>
        </ul>
        <h2>ساعات العمل</h2>
        <ul>
          <li>السبت - الخميس: 9:00 صباحًا - 9:00 مساءً</li>
          <li>الجمعة: 2:00 مساءً - 9:00 مساءً</li>
        </ul>
        <h2>تواصل معنا</h2>
        <p>يمكنك إرسال استفسارك من خلال النموذج التالي وسنقوم بالرد عليك في أقرب وقت ممكن.</p>
        <form>
          <div>
            <label for="name">الاسم</label>
            <input type="text" id="name" name="name" required />
          </div>
          <div>
            <label for="email">البريد الإلكتروني</label>
            <input type="email" id="email" name="email" required />
          </div>
          <div>
            <label for="phone">رقم الهاتف</label>
            <input type="tel" id="phone" name="phone" />
          </div>
          <div>
            <label for="message">الرسالة</label>
            <textarea id="message" name="message" rows="5" required></textarea>
          </div>
          <div>
            <button type="submit">إرسال</button>
          </div>
        </form>
      `,
      metaTitle: 'اتصل بنا | متجر الإلكترونيات',
      metaDescription: 'تواصل معنا عبر الهاتف أو البريد الإلكتروني أو نموذج الاتصال',
      isActive: true,
      showInFooter: true,
      showInHeader: true,
      footerSection: 'quick_links',
      order: 2
    });
    
    // إنشاء صفحة "سياسة الخصوصية"
    await createPage({
      title: 'سياسة الخصوصية',
      slug: 'privacy-policy',
      content: `
        <h1>سياسة الخصوصية</h1>
        <p>نحن نقدر خصوصيتك ونلتزم بحماية بياناتك الشخصية. تصف سياسة الخصوصية هذه كيفية جمع واستخدام وحماية معلوماتك الشخصية عند استخدام موقعنا الإلكتروني.</p>
        <h2>المعلومات التي نجمعها</h2>
        <p>قد نقوم بجمع المعلومات التالية:</p>
        <ul>
          <li>الاسم والعنوان وعنوان البريد الإلكتروني ورقم الهاتف</li>
          <li>معلومات الدفع والفواتير</li>
          <li>معلومات حول تفضيلاتك واهتماماتك</li>
          <li>معلومات أخرى تقدمها لنا طواعية</li>
        </ul>
        <h2>كيفية استخدام المعلومات</h2>
        <p>نستخدم المعلومات التي نجمعها للأغراض التالية:</p>
        <ul>
          <li>توفير وإدارة وتحسين خدماتنا</li>
          <li>معالجة طلباتك وتقديم المنتجات والخدمات التي تطلبها</li>
          <li>التواصل معك بشأن طلباتك واستفساراتك</li>
          <li>إرسال معلومات تسويقية إذا اخترت تلقيها</li>
        </ul>
        <h2>حماية المعلومات</h2>
        <p>نحن نتخذ تدابير أمنية مناسبة لحماية معلوماتك الشخصية من الوصول غير المصرح به أو التغيير أو الإفصاح أو الإتلاف.</p>
        <h2>الإفصاح عن المعلومات</h2>
        <p>لن نبيع أو نؤجر أو نتبادل معلوماتك الشخصية مع أطراف ثالثة دون موافقتك، إلا في الحالات التالية:</p>
        <ul>
          <li>عندما يكون ذلك ضروريًا لتقديم الخدمات التي طلبتها</li>
          <li>للامتثال للقانون أو الاستجابة لإجراءات قانونية</li>
          <li>لحماية حقوقنا أو ممتلكاتنا أو سلامتنا أو سلامة الآخرين</li>
        </ul>
        <h2>حقوقك</h2>
        <p>لديك الحق في:</p>
        <ul>
          <li>الوصول إلى معلوماتك الشخصية التي نحتفظ بها</li>
          <li>طلب تصحيح أو تحديث معلوماتك الشخصية</li>
          <li>طلب حذف معلوماتك الشخصية</li>
          <li>الاعتراض على معالجة معلوماتك الشخصية</li>
          <li>سحب موافقتك على معالجة معلوماتك الشخصية</li>
        </ul>
        <h2>التغييرات على سياسة الخصوصية</h2>
        <p>قد نقوم بتحديث سياسة الخصوصية هذه من وقت لآخر. سنقوم بنشر أي تغييرات على هذه الصفحة وسنبلغك بالتغييرات الجوهرية.</p>
        <h2>اتصل بنا</h2>
        <p>إذا كان لديك أي أسئلة حول سياسة الخصوصية هذه، يرجى الاتصال بنا على:</p>
        <ul>
          <li>البريد الإلكتروني: info@example.com</li>
          <li>الهاتف: +966 12 345 6789</li>
        </ul>
      `,
      metaTitle: 'سياسة الخصوصية | متجر الإلكترونيات',
      metaDescription: 'تعرف على سياسة الخصوصية وكيفية حماية بياناتك الشخصية',
      isActive: true,
      showInFooter: true,
      showInHeader: false,
      footerSection: 'information',
      order: 3
    });
    
    // إنشاء صفحة "شروط الاستخدام"
    await createPage({
      title: 'شروط الاستخدام',
      slug: 'terms',
      content: `
        <h1>شروط الاستخدام</h1>
        <p>يرجى قراءة شروط الاستخدام هذه بعناية قبل استخدام موقعنا الإلكتروني. باستخدام الموقع، فإنك توافق على الالتزام بهذه الشروط.</p>
        <h2>استخدام الموقع</h2>
        <p>يجب عليك استخدام موقعنا بطريقة قانونية وأخلاقية وبما يتوافق مع هذه الشروط. يحظر عليك:</p>
        <ul>
          <li>استخدام الموقع بطريقة قد تعطل أو تضر بالموقع أو بإمكانية وصول الآخرين إليه</li>
          <li>استخدام الموقع لأي غرض غير قانوني أو احتيالي</li>
          <li>جمع أو استخراج أي معلومات من الموقع بدون إذن</li>
          <li>محاولة الوصول غير المصرح به إلى أنظمتنا أو شبكاتنا</li>
        </ul>
        <h2>المنتجات والخدمات</h2>
        <p>نبذل قصارى جهدنا لضمان دقة المعلومات المتعلقة بالمنتجات والخدمات المعروضة على موقعنا. ومع ذلك، لا نضمن أن جميع المعلومات دقيقة أو كاملة أو حديثة.</p>
        <p>نحتفظ بالحق في تغيير أسعار المنتجات والخدمات في أي وقت دون إشعار مسبق.</p>
        <h2>الطلبات والدفع</h2>
        <p>عند تقديم طلب عبر موقعنا، فإنك تقدم عرضًا لشراء المنتجات أو الخدمات المحددة. نحتفظ بالحق في قبول أو رفض طلبك.</p>
        <p>يجب عليك تقديم معلومات دقيقة وكاملة عند إجراء عملية شراء.</p>
        <h2>التسليم والإرجاع</h2>
        <p>سنبذل قصارى جهدنا لتسليم المنتجات في الوقت المحدد. ومع ذلك، لا نضمن مواعيد التسليم.</p>
        <p>يمكنك إرجاع المنتجات وفقًا لسياسة الإرجاع الخاصة بنا.</p>
        <h2>الملكية الفكرية</h2>
        <p>جميع المحتويات الموجودة على موقعنا، بما في ذلك النصوص والصور والشعارات والرسومات، هي ملكية لنا أو لمرخصينا وتخضع لحماية حقوق الملكية الفكرية.</p>
        <p>لا يجوز لك نسخ أو توزيع أو نشر أو تعديل أي محتوى من موقعنا بدون إذن كتابي مسبق.</p>
        <h2>إخلاء المسؤولية</h2>
        <p>يتم توفير موقعنا "كما هو" و"كما هو متاح" دون أي ضمانات من أي نوع، صريحة أو ضمنية.</p>
        <p>لن نكون مسؤولين عن أي أضرار مباشرة أو غير مباشرة أو عرضية أو خاصة أو تبعية ناتجة عن استخدامك للموقع.</p>
        <h2>التغييرات على الشروط</h2>
        <p>قد نقوم بتحديث شروط الاستخدام هذه من وقت لآخر. سنقوم بنشر أي تغييرات على هذه الصفحة وسنبلغك بالتغييرات الجوهرية.</p>
        <h2>اتصل بنا</h2>
        <p>إذا كان لديك أي أسئلة حول شروط الاستخدام هذه، يرجى الاتصال بنا على:</p>
        <ul>
          <li>البريد الإلكتروني: info@example.com</li>
          <li>الهاتف: +966 12 345 6789</li>
        </ul>
      `,
      metaTitle: 'شروط الاستخدام | متجر الإلكترونيات',
      metaDescription: 'تعرف على شروط استخدام موقعنا الإلكتروني',
      isActive: true,
      showInFooter: true,
      showInHeader: false,
      footerSection: 'information',
      order: 4
    });
    
    console.log('Default pages created successfully');
  } catch (error) {
    console.error('Error creating default pages:', error);
    throw error;
  }
};
